/**
 * Copy strings for the AI copilot UX (cards, banners, chips, fallbacks).
 * Triggers themselves emit headline/evidence as plain strings since those
 * are domain-data shaped, not translatable boilerplate. Anything that does
 * NOT depend on entity state lives here.
 */
export const copilot = {
  variant: {
    recommendation: 'Recommendation',
    action: 'Action ready',
    insight: 'Insight',
  },
  dismiss: {
    accessibilityLabel: 'Dismiss suggestion',
  },
  rationale: {
    label: 'Why this',
    skillLabel: 'Why this skill',
    riskLabel: 'Why this risk level',
    notProvided: 'Rationale not provided by the model.',
  },
  followUp: {
    kicker: 'AI is drafting…',
    loadingCaption:
      'Picking the right skill, risk, and urgency from this item’s notes.',
    successKicker: 'AI suggestion ready',
    idleBodyWithAsset:
      'AI can draft a follow-up job for this item on {{assetName}} — picking the right skill, risk, and urgency before you accept.',
    idleBodyNoAsset:
      'AI can draft a follow-up job for this item — picking the right skill, risk, and urgency before you accept.',
    suggestCta_one: 'Suggest with AI · {{count}} credit',
    suggestCta_other: 'Suggest with AI · {{count}} credits',
    createCta: 'Create follow-up',
    whyExpand: 'Why this?',
    whyCollapse: 'Hide why',
    discard: 'Discard',
    errorFallback: 'AI could not analyse this item.',
    fallbackCta: 'Create draft manually',
    skillNotMatched: 'Skill not matched',
    skillNotInCatalogWarning:
      'AI proposed a skill not in your catalog. Pick one manually on the next screen.',
  },
  riskLabel: {
    low: 'Low risk',
    medium: 'Medium risk',
    high: 'High risk',
    critical: 'Critical risk',
  },
  urgencyLabel: {
    standard: 'Standard',
    priority: 'Priority',
    urgent: 'Urgent',
  },
  confidence: {
    high: 'High confidence',
    medium: 'Medium confidence',
    low: 'Verify before accepting',
  },
  dock: {
    openAssistant: 'Open assistant',
    expandAssistant: 'Expand assistant',
    assistantTitle: 'Assistant',
    closeAssistant: 'Close assistant',
    loadingConversation: 'Loading conversation...',
    assistantEmptyTitle: 'Ask the assistant',
    assistantEmptyBody: 'Use the same operational assistant from inside this side panel.',
    actorYou: 'You',
    actorAssistant: 'Assistant',
    dictationInserting: 'Inserting transcript…',
    dictationFinishing: 'Finishing dictation…',
    dictationHeard: 'Heard: {{transcript}}',
    dictationListening: 'Listening…',
    messageInputLabel: 'Message the assistant',
    messagePlaceholder: 'Ask a question or tell the assistant what to do',
    stopDictation: 'Stop dictation',
    dictateMessage: 'Dictate message',
    startNewThread: 'Start new assistant thread',
    newThread: 'New thread',
    sendAssistantMessage: 'Send assistant message',
    sending: 'Sending...',
    send: 'Send',
    noSuggestionsWithAsk: 'No suggestions right now. Ask the assistant below.',
    noSuggestions: 'No suggestions right now.',
    openAssistantInput: 'Open assistant input',
    askPlaceholder: 'Ask the assistant…',
    dictateAssistantMessage: 'Dictate assistant message',
  },
  photoFixSuggestion: {
    idleHeaderText: 'Use the photo to draft a fix',
    idleHint: 'AI looks at the photo and the inspector notes to suggest a title, scope, priority, and rough cost.',
    suggestA11y_one: 'Suggest fix from photo. Costs {{count}} credit.',
    suggestA11y_other: 'Suggest fix from photo. Costs {{count}} credits.',
    suggestButton_one: 'Suggest fix from photo ({{count}} credit)',
    suggestButton_other: 'Suggest fix from photo ({{count}} credits)',
    loadingStage1: 'Reading the photo…',
    loadingStage2: 'Spotting the likely fault…',
    loadingStage3: 'Drafting the fix…',
    loadingStage4: 'Almost there…',
    loadingHint: 'Looking at the photo takes a few seconds — you can keep working.',
    retryA11y: 'Retry fix suggestion',
    tryAgain: 'Try again',
    headerEyebrow: 'AI fix recommendation · {{pct}}% confident',
    priorityHigh: 'High',
    priorityMedium: 'Medium',
    priorityLow: 'Low',
    priorityChip: '{{level}} priority',
    safetyCallouts: 'Safety callouts',
    useThisFixA11y: 'Use this fix as the job draft',
    useThisFix: 'Use this fix',
    discardA11y: 'Discard this suggestion',
    discard: 'Discard',
  },
} as const;
